import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { useAuthStore } from '../../../auth/state';
import { 
  FaBookMedical,
  FaUserMd,
  FaBell,
  FaArrowRight
} from 'react-icons/fa';
import './updates.css';

function Updates() {
  const { user } = useAuthStore();
  const { category, updateId } = useParams();
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [iframeUrls, setIframeUrls] = useState({
    step1: 'https://wry-drill-0b1.notion.site/ebd/1c7db17e1b8e80508752e73b57450fef',
    step2: 'https://wry-drill-0b1.notion.site/ebd/1c7db17e1b8e8097881ece0fb46c90dd',
    other: 'https://wry-drill-0b1.notion.site/ebd/1c7db17e1b8e8095843cf249e70cef3a'
  });
  
  // # Create ref for iframe
  const iframeRef = useRef(null);

  // # Add animation state
  const [hoveredCard, setHoveredCard] = useState(null);

  // # Define update sections with icons and descriptions
  const updateSections = [
    {
      id: 'step1',
      title: 'USMLE Step 1 Updates',
      icon: <FaBookMedical className="ext_upd_section-icon" />,
      description: 'Latest updates and changes to Step 1 content',
      iframeUrl: iframeUrls.step1
    },
    {
      id: 'step2',
      title: 'USMLE Step 2 Updates',
      icon: <FaUserMd className="ext_upd_section-icon" />,
      description: 'Latest updates and changes to Step 2 content',
      iframeUrl: iframeUrls.step2
    },
    {
      id: 'other',
      title: 'Other Updates',
      icon: <FaBell className="ext_upd_section-icon" />,
      description: 'General platform updates and announcements',
      iframeUrl: iframeUrls.other
    }
  ];

  // # Determine if we're on a specific update page
  const isSpecificUpdate = !!category && !!updateId;
  
  // # Get current section if we're on a specific update page
  const currentSection = isSpecificUpdate 
    ? updateSections.find(section => section.id === category) 
    : null;

  // # Load user theme preferences
  useEffect(() => {
    const loadThemePreference = async () => {
      try {
        if (user?.uid) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setIsDarkMode(userData.themes?.gen_theme === 'dark');
          }
        }
      } catch (error) {
        console.error('Error loading theme preference:', error);
      }
    };

    loadThemePreference();
  }, [user]);

  // # Update iframe URLs with theme parameters when theme changes
  useEffect(() => {
    const updatedUrls = {};
    
    Object.keys(iframeUrls).forEach(key => {
      const baseUrl = iframeUrls[key];
      const urlObj = new URL(baseUrl);
      
      // # Set theme parameter based on current theme
      urlObj.searchParams.set('theme', isDarkMode ? 'dark' : 'light');
      
      // # Force dark/light mode in notion
      urlObj.searchParams.set('mode', isDarkMode ? 'dark' : 'light');
      
      // # Add parameters to disable features that might cause errors
      urlObj.searchParams.set('disableDatabase', 'true');
      urlObj.searchParams.set('disableSqlite', 'true');
      urlObj.searchParams.set('disableStorage', 'true');
      urlObj.searchParams.set('disableCookies', 'true');
      
      updatedUrls[key] = urlObj.toString();
    });
    
    setIframeUrls(updatedUrls);
  }, [isDarkMode]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Apply theme directly to iframe if on specific page
      if (isSpecificUpdate && iframeRef.current) {
        applyThemeToIframe(iframeRef.current, newTheme);
      }
      
      if (user?.uid) {
        await setDoc(doc(db, 'users', user.uid), {
          themes: { gen_theme: newTheme ? 'dark' : 'light' }
        }, { merge: true });
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    }
  };

  // # Function to apply theme directly to iframe
  const applyThemeToIframe = (iframe, darkMode) => {
    if (iframe) {
      try {
        // # Update iframe data-theme attribute
        iframe.setAttribute('data-theme', darkMode ? 'dark' : 'light');
        
        // # Update iframe style - remove filters, use proper CSS variables
        iframe.style.colorScheme = darkMode ? 'dark' : 'light';
        iframe.style.backgroundColor = darkMode ? '#121212' : 'white';
        
        // # Attempt to inject theme styles into the iframe
        setTimeout(() => {
          try {
            if (iframe && iframe.contentDocument) {
              const style = document.createElement('style');
              if (darkMode) {
                // # Dark mode styles without filters
                style.textContent = `
                  /* # Dark mode base styles */
                  body, html {
                    background-color: #121212 !important;
                    color-scheme: dark !important;
                    color: #e0e0e0 !important;
                  }
                  
                  /* # Dark mode text colors */
                  h1, h2, h3, h4, h5, h6, p, span, div {
                    color: #e0e0e0 !important;
                  }
                  
                  /* # Dark mode backgrounds */
                  .notion-page-content,
                  .notion-page-block {
                    background-color: #121212 !important;
                  }
                  
                  /* # Dark mode for block elements */
                  .notion-callout-block {
                    background-color: #1a1a1a !important;
                    border-color: rgba(255, 255, 255, 0.05) !important;
                  }
                  
                  /* # Tables in dark mode */
                  .notion-collection-table {
                    background-color: #121212 !important;
                    border-color: rgba(255, 255, 255, 0.05) !important;
                  }
                  
                  .notion-collection-table th,
                  .notion-collection-table td {
                    border-color: rgba(255, 255, 255, 0.05) !important;
                    color: #e0e0e0 !important;
                  }
                  
                  /* # Links in dark mode */
                  a, a:visited {
                    color: #64B5F6 !important;
                  }
                `;
              } else {
                // # Light mode styles
                style.textContent = `
                  body, html {
                    background-color: white !important;
                    color-scheme: light !important;
                    color: #2c3e50 !important;
                  }
                  
                  h1, h2, h3, h4, h5, h6, p, span, div {
                    color: #2c3e50 !important;
                  }
                  
                  /* # Light mode backgrounds */
                  .notion-page-content,
                  .notion-page-block {
                    background-color: white !important;
                  }
                  
                  /* # Light mode for block elements */
                  .notion-callout-block {
                    background-color: #f8f9fa !important;
                    border-color: rgba(55, 53, 47, 0.1) !important;
                  }
                  
                  /* # Tables in light mode */
                  .notion-collection-table {
                    background-color: white !important;
                    border-color: rgba(55, 53, 47, 0.1) !important;
                  }
                  
                  a, a:visited {
                    color: #0b6bcb !important;
                  }
                `;
              }
              iframe.contentDocument.head.appendChild(style);
            }
          } catch (e) {
            // # Silently fail if cross-origin issues prevent access
          }
        }, 1000);
      } catch (e) {
        // # Silently fail if cross-origin issues prevent access
      }
    }
  };

  // # Handle iframe load events
  const handleIframeLoad = () => {
    if (iframeRef.current) {
      try {
        // # Apply theme directly to iframe
        applyThemeToIframe(iframeRef.current, isDarkMode);
        
        // # Add mobile-specific meta tag for proper scaling
        const meta = document.createElement('meta');
        meta.name = 'viewport';
        meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
        
        // # Try to access iframe content to inject error suppression and styling
        const style = document.createElement('style');
        style.textContent = `
          /* # Hide error messages and UI elements that might expose URLs */
          .notion-overlay-container, 
          .notion-login-prompt,
          .notion-help-button,
          .notion-cursor-listener,
          .notion-frame > div[style*="fixed"],
          .notion-frame > div[style*="absolute"],
          .notion-topbar,
          .notion-selectable[data-block-id],
          .notion-link-token,
          .notion-collection-card__content,
          .notion-header__icon-wrapper {
            display: none !important;
          }
          
          /* # Force theme colors and prevent overflow */
          body, html {
            background-color: ${isDarkMode ? '#121212' : 'white'} !important;
            color-scheme: ${isDarkMode ? 'dark' : 'light'} !important;
            color: ${isDarkMode ? '#e0e0e0' : '#2c3e50'} !important;
            overflow-x: hidden !important;
            width: 100% !important;
            max-width: 100vw !important;
          }

          /* # Ensure content fits mobile screens */
          img, video, iframe {
            max-width: 100% !important;
            height: auto !important;
          }

          /* # Improve text readability on mobile */
          p, div {
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
          }
        `;
        
        // # Attempt to inject the style into the iframe
        setTimeout(() => {
          try {
            if (iframeRef.current && iframeRef.current.contentDocument) {
              iframeRef.current.contentDocument.head.appendChild(meta);
              iframeRef.current.contentDocument.head.appendChild(style);
            }
          } catch (e) {
            // # Silently fail if cross-origin issues prevent access
          }
        }, 1000);
      } catch (e) {
        // # Silently fail if cross-origin issues prevent access
      }
    }
  };

  // # Handle card hover
  const handleCardHover = (sectionId) => {
    setHoveredCard(sectionId);
  };

  // # Handle card click with ripple effect
  const handleCardClick = (sectionId, event) => {
    // # Create ripple element
    const ripple = document.createElement('div');
    ripple.className = 'ext_upd_ripple';
    
    // # Get click coordinates
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    // # Position ripple
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    
    // # Add ripple to card
    event.currentTarget.appendChild(ripple);
    
    // # Remove ripple after animation
    setTimeout(() => {
      ripple.remove();
      navigate(`/community/updates/${sectionId}/view`);
    }, 600);
  };

  // # If we're on a specific update page, render the full-page iframe
  if (isSpecificUpdate && currentSection) {
    return (
      <div className={`ext_upd_fullpage-container ${isDarkMode ? 'ext_upd_dark-mode' : ''}`}>
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
        
        <div className="ext_upd_fullpage-iframe-container">
          {/* # Error suppressor element */}
          <div className="ext_upd_error-suppressor" aria-hidden="true"></div>
          
          <iframe
            ref={iframeRef}
            src={currentSection.iframeUrl}
            title={currentSection.title}
            className={isDarkMode ? 'ext_upd_dark-mode-iframe' : 'ext_upd_light-mode-iframe'}
            allowFullScreen
            referrerPolicy="no-referrer"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            data-theme={isDarkMode ? 'dark' : 'light'}
            style={{
              colorScheme: isDarkMode ? 'dark' : 'light',
              backgroundColor: isDarkMode ? '#121212' : 'white'
            }}
            loading="lazy"
            importance="high"
            onLoad={handleIframeLoad}
          />
        </div>
      </div>
    );
  }

  // # Render section cards
  return (
    <div className={`ext_upd_page-container ${isDarkMode ? 'ext_upd_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <main className="ext_upd_main-content">
        <div className="ext_upd_header">
          <h1>Content Updates</h1>
          <p>Stay informed about the latest updates and changes to our study materials</p>
        </div>

        <div className="ext_upd_sections-grid">
          {updateSections.map((section, index) => (
            <div 
              key={index}
              className={`ext_upd_section-card ${hoveredCard === section.id ? 'ext_upd_card-hovered' : ''}`}
              onClick={(e) => handleCardClick(section.id, e)}
              onMouseEnter={() => handleCardHover(section.id)}
              onMouseLeave={() => handleCardHover(null)}
            >
              <div className="ext_upd_icon-container">
                {section.icon}
              </div>
              <h3>{section.title}</h3>
              <p>{section.description}</p>
              
              <div className="ext_upd_view-button">
                <span>View Updates</span>
                <FaArrowRight className="ext_upd_arrow-icon" />
              </div>
            </div>
          ))}
        </div>
      </main>

      <DashboardFooter />
      
      {/* # Add styles for ripple effect */}
      <style>
        {`
          .ext_upd_ripple {
            position: absolute;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.7);
            transform: scale(0);
            animation: ripple 0.6s linear;
            pointer-events: none;
          }

          @keyframes ripple {
            to {
              transform: scale(4);
              opacity: 0;
            }
          }
        `}
      </style>
    </div>
  );
}

export default Updates; 